import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';

/**
 * Safely acquire a token silently with proper account handling
 * This utility prevents the "no_account_error" by ensuring an account exists before calling acquireTokenSilent
 * 
 * @param instance - The MSAL instance
 * @param accounts - Optional accounts array from useMsal hook
 * @param scopes - Optional scopes array (defaults to User.Read and User.Read.All)
 * @returns Promise with the token response
 * @throws Error if no accounts are available
 */
export const safeAcquireTokenSilent = async (
  instance: IPublicClientApplication,
  accounts?: AccountInfo[],
  scopes?: string[]
) => {
  // Get accounts from instance if not provided
  let currentAccounts = accounts || instance.getAllAccounts();
  
  // Ensure we have accounts
  if (!currentAccounts || currentAccounts.length === 0) {
    throw new Error('No authenticated accounts available. Please sign in.');
  }

  // Use active account or first account
  const account = instance.getActiveAccount() || currentAccounts[0];
  
  if (!account) {
    throw new Error('No account object available for token acquisition');
  }

  // Acquire token silently
  const tokenResponse = await instance.acquireTokenSilent({
    scopes: scopes || ['User.Read', 'User.Read.All'],
    account: account,
  });

  return tokenResponse;
};

/**
 * Get the current authenticated account
 * 
 * @param instance - The MSAL instance
 * @returns The active account or first available account, or null if none exist
 */
export const getCurrentAccount = (instance: IPublicClientApplication): AccountInfo | null => {
  const activeAccount = instance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const accounts = instance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
};

/**
 * Check if user is authenticated
 * 
 * @param instance - The MSAL instance
 * @returns true if user has an authenticated account
 */
export const isUserAuthenticated = (instance: IPublicClientApplication): boolean => {
  const accounts = instance.getAllAccounts();
  return accounts.length > 0;
};
